"use client";

import type { PublicationType } from "agir-types";
import type { FastCommentsSSO } from "fastcomments-typescript";
import type { ReactNode } from "react";

import { clsx } from "clsx";
import { FastComments } from "fastcomments-nextjs";
import { useEffect, useState } from "react";

import { Button } from "@/src/components/ui/Button/Button";
import { useMounted } from "@/src/hooks/useMounted";
import {
  alwaysConsent,
  useConsent,
} from "@/src/infrastructure/cookieconsent/consent";
import {
  fastCommentsConfig,
  fastCommentsCookie,
  fastCommentsUrlId,
} from "@/src/infrastructure/fastcomments/fastComments";
import { withWindow } from "@/src/withWindow";

export interface CommentsProps {
  readonly id: string;
  readonly readonly?: boolean;
  readonly sso?: FastCommentsSSO | undefined;
  readonly type: PublicationType;
}

export function Comments({
  id,
  readonly = false,
  sso,
  type,
}: CommentsProps): ReactNode {
  const mounted = useMounted();
  const consented = useConsent(fastCommentsCookie);
  const [allowedOnce, setAllowedOnce] = useState(false);
  const interactive = sso && !readonly;

  // An explicit choice in the preferences (accept/reject/save) overrides the
  // temporary "accepter cette fois" bypass. Otherwise, rejecting FastComments
  // after clicking it once would leave the widget mounted forever.
  useEffect(() => {
    const { window } = withWindow();
    if (!window) {
      return;
    }
    function reset(): void {
      setAllowedOnce(false);
    }
    window.addEventListener("cc:onChange", reset);
    window.addEventListener("cc:onConsent", reset);
    return (): void => {
      window.removeEventListener("cc:onChange", reset);
      window.removeEventListener("cc:onConsent", reset);
    };
  }, []);

  // 406 pixels seems to be the minimum widget size when the comment form is shown
  const minHeight = interactive ? "min-h-[406px]" : "min-h-37.5";

  if (mounted && !consented && !allowedOnce) {
    return (
      <div
        className={clsx(
          "bubble-2xl bg-gray-darker flex items-center justify-center p-8 text-white",
          minHeight,
        )}
      >
        <div className="text-center">
          <p className="mb-5 text-sm">
            Pour accéder aux commentaires, accepter les cookies FastComments
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              className="normal-case"
              onClick={() => {
                setAllowedOnce(true);
              }}
              size="medium"
            >
              Accepter cette fois
            </Button>
            <Button
              className="bg-gray-lighter hover:bg-gray-lightest text-gray-darker normal-case"
              onClick={() => {
                alwaysConsent(fastCommentsCookie);
              }}
              size="medium"
            >
              Toujours accepter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx("relative", minHeight)} data-nosnippet>
      {mounted ? (
        <FastComments
          {...fastCommentsConfig}
          {...(interactive
            ? { readonly: false, sso }
            : { disableVoting: true, readonly: true })}
          urlId={fastCommentsUrlId(type, id)}
        />
      ) : null}
    </div>
  );
}
